/**
 * Codigos de barras EAN-13 y EAN-8.
 *
 * LO QUE HAY QUE ENTENDER ANTES DE TOCAR ESTE ARCHIVO
 * ---------------------------------------------------
 * Un codigo EAN-13 real NO se inventa. Los rangos de numeracion los asigna GS1
 * a cambio de una membresia anual, y cada prefijo pertenece a una empresa
 * concreta. Si la tienda se inventa codigos al azar para su granel, tarde o
 * temprano uno de ellos coincidira con el de un producto real y la caja cobrara
 * jitomate cuando escaneen un shampoo.
 *
 * Por eso existe un rango RESERVADO. La especificacion general de GS1 aparta
 * los prefijos 02 y 20 a 29 para "circulacion restringida dentro de una
 * empresa": son codigos que valen dentro de la tienda y que ningun fabricante
 * puede usar jamas. Es exactamente el caso de un abarrotes que necesita
 * etiquetar sus chiles secos y sus semillas.
 *
 * Todo lo que este modulo genera cae en ese rango. Es la diferencia entre un
 * generador de juguete y uno que se puede dejar operando años.
 *
 * El modulo es puro: sin red, sin DOM, sin base de datos. Se puede probar
 * contra los vectores publicados de la norma, y eso es justo lo que hace
 * `barcode.test.ts`.
 *
 * Los codigos que NO son EAN/UPC —alfanumericos, folios, identificadores
 * propios— se imprimen como Code 128, y esa codificacion vive en `code128.ts`,
 * de donde se reusa aqui. Una sola tabla de la norma para todo el proyecto.
 */
import { CODE128_QUIET_MODULES, code128Pattern, isCode128B } from './code128.js';
// ---------------------------------------------------------------------------
// Rango reservado
// ---------------------------------------------------------------------------
/**
 * Prefijos que GS1 aparta para uso interno de una empresa.
 *
 * Se ofrece del 20 al 29 y se omite el 02 a proposito: aunque tambien es
 * interno, empieza con cero y muchos lectores baratos lo confunden con un
 * UPC-A de 12 digitos al que le sobra un digito.
 */
export const INTERNAL_PREFIXES = ['20', '21', '22', '23', '24', '25', '26', '27', '28', '29'];
export function isInternalPrefix(value) {
    return INTERNAL_PREFIXES.includes(value);
}
/** True si el codigo pertenece al rango interno y por tanto lo genero la tienda. */
export function isInternalBarcode(code) {
    const digits = onlyDigits(code);
    return digits.length === 13 && isInternalPrefix(digits.slice(0, 2));
}
// ---------------------------------------------------------------------------
// Digito verificador
// ---------------------------------------------------------------------------
function onlyDigits(value) {
    return value.replace(/\D/g, '');
}
/**
 * Digito verificador del sistema GS1 (modulo 10 con pesos 3 y 1).
 *
 * Se recorre de DERECHA a IZQUIERDA dando peso 3 al ultimo digito. Hacerlo al
 * reves funciona por casualidad cuando la longitud es par y falla cuando es
 * impar, que es como se cuelan los errores en las implementaciones caseras:
 * EAN-13 sale bien y EAN-8 sale mal.
 *
 * @param digits los digitos SIN el verificador (12 para EAN-13, 7 para EAN-8)
 */
export function gs1CheckDigit(digits) {
    const clean = onlyDigits(digits);
    let sum = 0;
    for (let i = 0; i < clean.length; i++) {
        const digit = clean.charCodeAt(clean.length - 1 - i) - 48;
        // El de mas a la derecha pesa 3, y de ahi se alterna.
        sum += i % 2 === 0 ? digit * 3 : digit;
    }
    return (10 - (sum % 10)) % 10;
}
/** Agrega el digito verificador a una base de 12 digitos (EAN-13) o 7 (EAN-8). */
export function withCheckDigit(base) {
    const clean = onlyDigits(base);
    return `${clean}${gs1CheckDigit(clean)}`;
}
export function isValidEan13(code) {
    const clean = onlyDigits(code);
    if (clean.length !== 13)
        return false;
    return gs1CheckDigit(clean.slice(0, 12)) === clean.charCodeAt(12) - 48;
}
export function isValidEan8(code) {
    const clean = onlyDigits(code);
    if (clean.length !== 8)
        return false;
    return gs1CheckDigit(clean.slice(0, 7)) === clean.charCodeAt(7) - 48;
}
/** UPC-A son 12 digitos; equivale a un EAN-13 que empieza con cero. */
export function isValidUpcA(code) {
    const clean = onlyDigits(code);
    if (clean.length !== 12)
        return false;
    return gs1CheckDigit(clean.slice(0, 11)) === clean.charCodeAt(11) - 48;
}
export function detectSymbology(code) {
    const clean = onlyDigits(code);
    if (clean.length === 13 && isValidEan13(clean))
        return 'ean13';
    if (clean.length === 12 && isValidUpcA(clean))
        return 'upca';
    if (clean.length === 8 && isValidEan8(clean))
        return 'ean8';
    return 'other';
}
/**
 * Deja el codigo en su forma canonica para guardarlo y buscarlo.
 *
 * UPC-A y EAN-13 son el MISMO codigo: un UPC-A de 12 digitos es un EAN-13 con
 * un cero delante. El detalle importa porque los lectores no se ponen de
 * acuerdo: unos entregan 12 digitos y otros los 13 con el cero. Si el producto
 * se dio de alta con una forma y se escanea con la otra, la busqueda falla y el
 * cajero jura que el sistema "perdio" el producto.
 *
 * Guardando siempre la forma de 13 digitos, ambas lecturas encuentran lo mismo.
 */
export function normalizeBarcode(code) {
    const trimmed = code.trim();
    // Solo se normaliza lo que es un codigo numerico. Un identificador propio de
    // la tienda como "GRANEL-01" tiene que sobrevivir intacto: quitarle lo no
    // numerico lo convertiria en "01" y en un choque con cualquier otro.
    if (!/^[\d\s-]+$/.test(trimmed))
        return trimmed;
    const clean = onlyDigits(trimmed);
    if (clean.length === 0)
        return trimmed;
    if (clean.length === 12 && isValidUpcA(clean))
        return `0${clean}`;
    return clean;
}
/**
 * Todas las formas con que un lector puede entregar el mismo codigo.
 * Se usa al buscar, para no depender de como venga configurado el aparato.
 */
export function barcodeVariants(code) {
    const trimmed = code.trim();
    const variants = new Set([trimmed]);
    if (!/^[\d\s-]+$/.test(trimmed))
        return [...variants];
    const clean = onlyDigits(trimmed);
    if (clean.length > 0)
        variants.add(clean);
    if (clean.length === 12 && isValidUpcA(clean))
        variants.add(`0${clean}`);
    if (clean.length === 13 && clean.startsWith('0') && isValidEan13(clean)) {
        variants.add(clean.slice(1));
    }
    return [...variants];
}
// ---------------------------------------------------------------------------
// Generacion de codigos internos
// ---------------------------------------------------------------------------
/**
 * Genera un EAN-13 interno a partir de un consecutivo.
 *
 * Estructura:  PP  NNNNNNNNNN  C
 *              |   |           +-- digito verificador
 *              |   +-------------- consecutivo de 10 digitos
 *              +------------------ prefijo interno (20-29)
 *
 * Diez digitos de consecutivo alcanzan para diez mil millones de productos:
 * la tienda se acabara antes que los codigos.
 */
export function generateInternalEan13(prefix, sequence) {
    if (!Number.isInteger(sequence) || sequence < 0) {
        throw new Error(`consecutivo invalido: ${sequence}`);
    }
    if (sequence > 9_999_999_999) {
        throw new Error('se agotaron los consecutivos de codigo interno');
    }
    const base = `${prefix}${String(sequence).padStart(10, '0')}`;
    return withCheckDigit(base);
}
/** Consecutivo que contiene un codigo interno, o null si no es de la tienda. */
export function sequenceFromInternal(code) {
    const clean = onlyDigits(code);
    if (clean.length !== 13 || !isInternalPrefix(clean.slice(0, 2)))
        return null;
    if (!isValidEan13(clean))
        return null;
    return Number(clean.slice(2, 12));
}
// ---------------------------------------------------------------------------
// Etiquetas de bascula: precio o peso incrustado
// ---------------------------------------------------------------------------
export const SCALE_MODES = ['off', 'price', 'weight'];
export const SCALE_MODE_LABELS = {
    off: 'Sin bascula etiquetadora',
    price: 'La bascula imprime el PRECIO en el codigo',
    weight: 'La bascula imprime el PESO en el codigo',
};
/**
 * Configuracion por omision.
 *
 * `PP IIIII VVVVV C` = 2 + 5 + 5 + 1 = 13 digitos.
 *
 * Es el reparto mas extendido en basculas etiquetadoras: cinco digitos de
 * articulo (hasta 99 999 productos) y cinco de valor, que dan hasta $999.99 de
 * precio o 99.999 kg de peso. Se deja configurable porque cada marca de
 * bascula reparte los digitos a su manera.
 */
export const DEFAULT_SCALE_CONFIG = {
    mode: 'off',
    prefix: '21',
    itemDigits: 5,
    valueDigits: 5,
};
/**
 * Interpreta una etiqueta de bascula.
 *
 * Devuelve null si el codigo no corresponde a la configuracion: eso significa
 * que es un producto normal y debe buscarse por catalogo como cualquier otro.
 *
 * Nota sobre el peso: la bascula imprime GRAMOS y el sistema trabaja en
 * milesimas de unidad. Para un producto que se vende por kilo, mil gramos son
 * mil milesimas, asi que la conversion es directa; se deja explicita de todos
 * modos para que se vea que no es una coincidencia.
 */
export function parseScaleBarcode(code, config) {
    if (config.mode === 'off')
        return null;
    const clean = onlyDigits(code.trim());
    const expected = config.prefix.length + config.itemDigits + config.valueDigits + 1;
    if (clean.length !== expected)
        return null;
    if (!clean.startsWith(config.prefix))
        return null;
    // Una etiqueta con el verificador mal es una lectura sucia, no un producto.
    if (!isValidEan13(clean))
        return null;
    const itemStart = config.prefix.length;
    const itemCode = clean.slice(itemStart, itemStart + config.itemDigits);
    const rawValue = Number(clean.slice(itemStart + config.itemDigits, expected - 1));
    if (!Number.isFinite(rawValue))
        return null;
    return {
        itemCode,
        priceCents: config.mode === 'price' ? rawValue : null,
        qtyMilli: config.mode === 'weight' ? rawValue : null,
    };
}
/** Construye la etiqueta que imprimiria la bascula. Sirve para probar y demostrar. */
export function buildScaleBarcode(itemCode, value, config) {
    const item = onlyDigits(itemCode).padStart(config.itemDigits, '0').slice(-config.itemDigits);
    const val = String(Math.max(0, Math.round(value)))
        .padStart(config.valueDigits, '0')
        .slice(-config.valueDigits);
    return withCheckDigit(`${config.prefix}${item}${val}`);
}
// ---------------------------------------------------------------------------
// Patron de barras
// ---------------------------------------------------------------------------
/**
 * Tablas de codificacion de la norma. Cada digito son siete modulos.
 *
 * En EAN-13, el PRIMER digito no se dibuja: se deduce de la combinacion de
 * paridades (L u G) con que se codifican los seis digitos de la izquierda.
 * Es la parte que mas se equivoca quien implementa esto por primera vez, y el
 * sintoma es un codigo que se lee en pantalla pero que ningun lector reconoce.
 */
const L_CODES = [
    '0001101', '0011001', '0010011', '0111101', '0100011',
    '0110001', '0101111', '0111011', '0110111', '0001011',
];
const G_CODES = [
    '0100111', '0110011', '0011011', '0100001', '0011101',
    '0111001', '0000101', '0010001', '0001001', '0010111',
];
const R_CODES = [
    '1110010', '1100110', '1101100', '1000010', '1011100',
    '1001110', '1010000', '1000100', '1001000', '1110100',
];
/** Paridad de los seis digitos izquierdos segun el primer digito. 0 = L, 1 = G. */
const PARITY = [
    '000000', '001011', '001101', '001110', '010011',
    '011001', '011100', '010101', '010110', '011010',
];
const GUARD_SIDE = '101';
const GUARD_CENTER = '01010';
function digitAt(code, index) {
    return code.charCodeAt(index) - 48;
}
/**
 * Convierte un EAN-13 valido en su patron de 95 modulos.
 *
 *   guarda(3) + 6 digitos(42) + guarda central(5) + 6 digitos(42) + guarda(3)
 */
export function ean13Pattern(code) {
    const clean = onlyDigits(code);
    if (!isValidEan13(clean)) {
        throw new Error(`EAN-13 invalido: ${code}`);
    }
    const parity = PARITY[digitAt(clean, 0)] ?? PARITY[0];
    let modules = GUARD_SIDE;
    for (let i = 1; i <= 6; i++) {
        const digit = digitAt(clean, i);
        const useG = parity.charAt(i - 1) === '1';
        modules += (useG ? G_CODES[digit] : L_CODES[digit]) ?? '';
    }
    modules += GUARD_CENTER;
    for (let i = 7; i <= 12; i++) {
        modules += R_CODES[digitAt(clean, i)] ?? '';
    }
    modules += GUARD_SIDE;
    return {
        modules,
        guardRanges: [
            { start: 0, end: 3 },
            { start: 45, end: 50 },
            { start: 92, end: 95 },
        ],
        text: { leading: clean.slice(0, 1), left: clean.slice(1, 7), right: clean.slice(7) },
        symbology: 'ean13',
        humanText: clean,
    };
}
/** EAN-8: guarda(3) + 4 digitos L(28) + guarda central(5) + 4 digitos R(28) + guarda(3). */
export function ean8Pattern(code) {
    const clean = onlyDigits(code);
    if (!isValidEan8(clean)) {
        throw new Error(`EAN-8 invalido: ${code}`);
    }
    let modules = GUARD_SIDE;
    for (let i = 0; i < 4; i++)
        modules += L_CODES[digitAt(clean, i)] ?? '';
    modules += GUARD_CENTER;
    for (let i = 4; i < 8; i++)
        modules += R_CODES[digitAt(clean, i)] ?? '';
    modules += GUARD_SIDE;
    return {
        modules,
        guardRanges: [
            { start: 0, end: 3 },
            { start: 31, end: 36 },
            { start: 64, end: 67 },
        ],
        text: { leading: '', left: clean.slice(0, 4), right: clean.slice(4) },
        symbology: 'ean8',
        humanText: clean,
    };
}
/**
 * Elige la codificacion y devuelve el patron. Lanza solo si de plano no hay
 * simbologia que lo represente (caracteres fuera del ASCII visible).
 *
 * El orden importa: un codigo que ES un EAN/UPC valido se dibuja como tal,
 * porque es lo que cualquier lector del mundo espera de un producto de tienda.
 * Todo lo demas —alfanumerico, numerico que no cuadra como EAN, un identificador
 * propio— cae a Code 128, que lo imprime igual.
 *
 * La codificacion Code 128 NO se reimplementa aqui: vive en `code128.ts`, que
 * ya la tenia para los folios de venta del ticket. Es la misma tabla de la norma
 * y el mismo verificador; tener dos seria invitar a que un dia difieran y una
 * etiqueta deje de escanear. Aqui solo se envuelve su salida en la forma comun
 * `BarcodePattern` para que el mismo componente dibuje EAN y Code 128.
 */
export function barcodePattern(code) {
    const normalized = normalizeBarcode(code);
    const clean = onlyDigits(normalized);
    // EAN/UPC SOLO si el codigo es puramente numerico. Sin esta guarda, un codigo
    // con letras como "V01-000047" se colaba a EAN: `onlyDigits` lo dejaba en
    // "01000047", que resulta ser un EAN-8 valido, y se dibujaba ESE —sin la "V"
    // ni el guion—, un simbolo que al escanearse abre otra cosa. `normalized ===
    // clean` es verdadero solo cuando no se cayo ni una letra ni un separador.
    if (normalized === clean) {
        if (clean.length === 13 && isValidEan13(clean))
            return ean13Pattern(clean);
        if (clean.length === 8 && isValidEan8(clean))
            return ean8Pattern(clean);
    }
    if (isCode128B(normalized)) {
        const encoded = code128Pattern(normalized);
        return {
            modules: encoded.modules,
            // Code 128 no tiene barras guarda: todas sus barras miden lo mismo.
            guardRanges: [],
            text: { leading: '', left: normalized, right: '' },
            symbology: 'code128',
            humanText: normalized,
        };
    }
    throw new Error(`no se puede imprimir como codigo de barras: "${code}"`);
}
/** True si el codigo se puede dibujar como simbolo. */
export function isPrintable(code) {
    try {
        barcodePattern(code);
        return true;
    }
    catch {
        return false;
    }
}
// ---------------------------------------------------------------------------
// Proporciones de impresion
// ---------------------------------------------------------------------------
/**
 * Medidas nominales de la norma, en milimetros, al 100 % de magnificacion.
 *
 * Las zonas de silencio no son decorativas: son parte del simbolo. Sin ellas el
 * lector no encuentra donde empieza el codigo, y ese es el motivo numero uno de
 * que una etiqueta impresa en casa no se deje escanear.
 */
export const BARCODE_GEOMETRY = {
    /** Ancho de un modulo al 100 %. */
    moduleMm: 0.33,
    /** Alto de las barras normales, en modulos. */
    barModules: 69,
    /** Cuanto sobresalen las barras guarda por debajo, en modulos. */
    guardExtraModules: 5,
    /** Zona de silencio izquierda de un EAN-13, en modulos. */
    quietLeftEan13: 11,
    /** Zona de silencio derecha de un EAN-13, en modulos. */
    quietRightEan13: 7,
    /** EAN-8 lleva siete modulos de silencio a cada lado. */
    quietEan8: 7,
    /** Code 128 exige diez modulos de silencio a cada lado; se reusa la constante
     *  de `code128.ts` para no tener el mismo minimo escrito en dos lugares. */
    quietCode128: CODE128_QUIET_MODULES,
    /**
     * Magnificacion minima que admite la norma. Por debajo de 80 % una impresora
     * domestica ya no resuelve los modulos y el codigo deja de leerse.
     */
    minMagnification: 0.8,
};
//# sourceMappingURL=barcode.js.map